import { useSiteLang } from '@/components/site/SiteLang';
import React, { useState, createContext } from 'react';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Building2, Globe2, Users,
  GraduationCap, ChevronDown, Target, BookOpen, Network, Eye,
  HelpCircle, CreditCard, Sparkles, Star, ArrowUpRight,
  KeyRound,
} from 'lucide-react';
import QualificationFormModal from '@/components/QualificationForm';
// Fotos reales del club (frontend/public/media y /team). Son de día y de resolución media:
// en el héroe van siempre bajo un degradado azul noche. Nunca se presentan como «sede».
const IMG = {
  hero: '/media/amaroo-sede.jpg',
  who: '/media/amaroo-vista.jpg',
  philosophy: '/media/amaroo-interior.jpg',
};
const TEAM_PHOTOS = [
  { name: 'Antonio Gimeno', img: '/team/antonio-gimeno.jpg' },
  { name: 'Daniel Serrano', img: '/team/daniel-serrano.jpg' },
  { name: 'Luna Trejos', img: '/team/luna-trejos.jpg' },
];


/* ─── TRANSLATIONS ─── */
const texts = {
  es: {
    nav: { cta: 'Hacer el test de idoneidad', membresias: 'Membresías', bootcamp: 'Bootcamp', howItWorks: 'Cómo funciona', contacto: 'Contacto', login: 'Iniciar sesión', acceder: 'Acceder', menu: 'Menú' },
    hero: {
      badge: 'Club privado',
      title: 'Freedom Homes',
      titleAccent: 'Club',
      subtitle: 'El mercado inmobiliario que nunca estuvo al alcance de todos. Ahora sí.',
      desc: 'Un club privado donde empresarios y profesionales acceden a oportunidades inmobiliarias reales fuera del mercado, se forman para entenderlas y eligen con criterio propio en cuáles participan. Aprende antes de decidir.',
      cta1: 'Hacer el test de idoneidad',
      cta2: 'Explorar el club',
      badges: ['Oportunidades off-market', 'Formación desde cero', 'Comunidad privada'],
    },
    market: {
      label: 'Evolución del mercado',
      title: 'Durante décadas, el mercado inmobiliario funcionó igual.',
      items: [
        { num: '01', title: 'El modelo tradicional', desc: 'Las mejores operaciones se cerraban entre pocos: quien tenía los contactos, el conocimiento y el tiempo. Al resto le llegaba lo que el mercado ya había filtrado.' },
        { num: '02', title: 'La nueva capa digital', desc: 'Registros digitales, blockchain y nuevos modelos de registro y gestión digital de la propiedad están cambiando cómo se estructura y se gestiona el sector inmobiliario.' },
        { num: '03', title: 'Aprende antes de decidir', desc: 'Se habla mucho del nuevo inmobiliario y se entiende poco. En el club primero te formas; después decides tú, con criterio propio.' },
      ],
    },
    who: {
      label: 'Para quién es',
      title: 'Un club de acceso restringido para personas que ya han',
      titleAccent: 'tomado una decisión.',
      items: [
        { title: 'Empresarios', desc: 'Quieres entender cómo está cambiando el sector inmobiliario y qué significa para tu negocio y tus decisiones.' },
        { title: 'Profesionales del sector', desc: 'Trabajas en el sector y ves cómo se digitaliza mientras la mayoría de tus colegas mira desde la orilla.' },
        { title: 'Mentes inquietas', desc: 'Sientes que las reglas del sector se están reescribiendo y quieres entenderlas con método y en buena compañía.' },
      ],
    },
    bootcamp: {
      label: 'Experiencia inmersiva',
      title: 'El Bootcamp Broker Digital',
      desc: 'Una experiencia inmersiva de 4 días para entender a fondo la nueva arquitectura del sector inmobiliario.',
      days: [
        { day: 'Día 1', title: 'Cambio de paradigma', desc: 'Comprender la transformación del sector inmobiliario.', icon: BookOpen },
        { day: 'Día 2', title: 'Marco jurídico y técnico', desc: 'Marcos jurídicos y técnicos de la tokenización inmobiliaria.', icon: Building2 },
        { day: 'Día 3', title: 'El día que más recuerdan', desc: 'Simulaciones, casos reales y networking con la cohorte.', icon: Globe2 },
        { day: 'Día 4', title: 'Certificación y plan de 30 días', desc: 'Tu certificación privada FHC y tu plan de acción para el primer mes.', icon: Star },
      ],
    },
    inside: {
      label: 'Dentro del club',
      title: 'Esto es lo que ocurre cuando entras.',
      items: [
        { title: 'Oportunidades fuera del mercado', desc: 'En el área privada se presentan oportunidades inmobiliarias reales que no llegan a los portales, con su documentación y su análisis. Tú eliges en qué participas.' },
        { title: 'Formación estratégica', desc: 'FHC Academy te lleva desde cero hasta entender una operación inmobiliaria con criterio propio. Primero aprendes; después decides.' },
        { title: 'Comunidad y encuentros', desc: 'Un club activo de miembros y profesionales, con sesiones en directo, eventos y bootcamps presenciales en la Costa Blanca.' },
      ],
    },
    philosophy: {
      label: 'Nuestra filosofía',
      title: 'Una sola cosa nos diferencia de cualquier otro club del sector.',
      quote: 'Aquí la membresía se gana por alineación, no se compra por impulso. Cada solicitud pasa por una evaluación real. No es un filtro de marketing — es la razón por la que los miembros que entran encuentran dentro exactamente lo que vinieron a buscar.',
      items: [
        { n: 'I', t: 'Claridad sobre complejidad', d: 'Simplificamos conceptos sofisticados sin perder su profundidad.' },
        { n: 'II', t: 'Acceso por alineación', d: 'La membresía se gana por alineación profesional, no se compra por impulso.' },
        { n: 'III', t: 'Perspectiva global', d: 'El sector inmobiliario ya no es local. Nuestra comunidad está abierta a profesionales de distintos países.' },
        { n: 'IV', t: 'Educación ante todo', d: 'La comprensión va primero. Siempre.' },
        { n: 'V', t: 'Comunidad como infraestructura', d: 'La red de miembros es tan valiosa como el conocimiento compartido.' },
        { n: 'VI', t: 'Transparencia', d: 'Lo que incluye cada membresía, explicado con claridad.' },
        { n: 'VII', t: 'Visión a largo plazo', d: 'Construimos para décadas, no para trimestres.' },
      ],
    },
    assessment: {
      label: 'Test de idoneidad',
      title: '¿Tienes el perfil para',
      titleAccent: 'formar parte?',
      desc: 'El acceso a Freedom Homes Club empieza con un test de idoneidad de menos de 2 minutos. Después eliges día y hora para una llamada con el equipo: te explicamos cada membresía, su cuota y qué nivel encaja contigo.',
      cta: 'Hacer el test de idoneidad',
    },
    faq: {
      label: 'FAQ',
      title: 'Preguntas frecuentes',
      payment: 'Métodos de pago:',
      paymentDesc: 'Tarjeta o transferencia bancaria.',
      items: [
        { q: '¿Cuál es la diferencia real entre las membresías Access, PRO y Premium?', a: 'Access te da la pertenencia al club, la comunidad privada y los contenidos introductorios. PRO añade la formación avanzada, las sesiones especializadas en directo y los recursos ampliados. Premium es el nivel completo: todo lo de PRO más el Bootcamp Broker Digital presencial, la certificación privada FHC y el acompañamiento grupal semanal.' },
        { q: '¿Qué tipo de oportunidades hay dentro del club?', a: 'Oportunidades inmobiliarias reales que el equipo y sus colaboradores trabajan fuera de los portales. Se presentan en el área privada con su documentación y su análisis, y cada miembro decide libremente si participa y en cuál, después de formarse. La membresía te da acceso al club; participar es siempre una decisión aparte y voluntaria.' },
        { q: '¿Cuánto cuesta la membresía?', a: 'Cada membresía tiene su cuota anual, que se renueva desde tu fecha de alta. Te la explicamos en una llamada breve con el equipo, después de hacer el test de idoneidad, para recomendarte el nivel que de verdad encaja contigo.' },
        { q: '¿Qué estoy pagando exactamente?', a: 'La cuota corresponde exclusivamente al acceso al club, la comunidad privada, los contenidos formativos, las sesiones y los eventos incluidos en el nivel contratado.' },
        { q: '¿La certificación es oficial?', a: 'La certificación FHC es privada e interna del club, sin equivalencia académica oficial.' },
        { q: '¿Necesito experiencia previa en tokenización o blockchain?', a: 'No. El programa está diseñado para guiarte desde cero. El punto de partida es entender el sector, no dominar la tecnología.' },
      ],
    },
    final: {
      title: 'El acceso a Freedom Homes Club comienza con una pregunta simple:',
      titleAccent: '¿Tienes el perfil?',
      desc: 'El test tarda menos de 2 minutos. Al terminarlo eliges día y hora para hablar con el equipo. Sin compromiso. Con claridad.',
      cta: 'Hacer el test de idoneidad',
      note: 'Tras el test podrás agendar tu llamada al momento.',
    },
    team: {
      label: 'Quién está detrás',
      title: 'Un equipo fundador que da la cara.',
      desc: 'Personas con nombre y apellidos que acompañan a cada miembro desde el primer día.',
      roles: ['CCO y Director Comercial', 'COO', 'Directora de Formación y Comunidad'],
    },
    imgAlt: {
      who: 'Villa de la Costa Blanca que ha acogido encuentros presenciales del club',
      philosophy: 'Interior de una villa de la Costa Blanca donde el club ha celebrado encuentros',
    },
  },
    en: {
    // Inglés británico (revisado 04/11/2026): property en vez de real estate, ortografía británica
    nav: { cta: 'Take the suitability test', membresias: 'Memberships', bootcamp: 'Bootcamp', howItWorks: 'How it works', contacto: 'Contact', login: 'Sign in', acceder: 'Log in', menu: 'Menu' },
    hero: {
      badge: 'Private Club',
      title: 'Freedom Homes',
      titleAccent: 'Club',
      subtitle: 'The property market that was never open to everyone. Now it is.',
      desc: 'A private club where entrepreneurs and professionals get access to genuine off-market property opportunities, learn how they work and decide, on their own judgement, which ones to take part in. Learn first, then decide.',
      cta1: 'Take the suitability test',
      cta2: 'Explore the club',
      badges: ['Off-market opportunities', 'Training from scratch', 'Private community'],
    },
    market: {
      label: 'A market in transition',
      title: 'The property market is entering a new phase.',
      items: [
        { num: '01', title: 'The old way', desc: 'The best deals were done between a few: those with the contacts, the know-how and the time. Everyone else got what the market had already passed over.' },
        { num: '02', title: 'The new digital layer', desc: 'Digital registries, blockchain and new ways of recording and managing property are changing how the sector is structured and run.' },
        { num: '03', title: 'Learn before you decide', desc: 'Everyone talks about the new property market; few really understand it. In the club you learn first and decide afterwards, on your own judgement.' },
      ],
    },
    who: {
      label: 'Who it’s for',
      title: 'Built for a particular kind of',
      titleAccent: 'professional.',
      items: [
        { title: 'Entrepreneurs', desc: 'You want to understand how property is changing and what that means for your business and your decisions.' },
        { title: 'Property professionals', desc: 'Estate agents, brokers and developers who can see the sector going digital and want to lead the change, not follow it.' },
        { title: 'The curious', desc: 'People who want to learn the new rules of the sector properly, and in good company.' },
      ],
    },
    bootcamp: {
      label: 'In person',
      title: 'The Digital Broker Bootcamp',
      desc: 'Four days in person to get a thorough grasp of how the property sector is being rebuilt.',
      days: [
        { day: 'Day 1', title: 'A shift in perspective', desc: 'Understanding how the property sector is changing.', icon: BookOpen },
        { day: 'Day 2', title: 'The legal and technical framework', desc: 'The legal and technical frameworks behind property tokenisation.', icon: Building2 },
        { day: 'Day 3', title: 'The day everyone remembers', desc: 'Simulations, real cases and networking with your cohort.', icon: Globe2 },
        { day: 'Day 4', title: 'Certification and a 30-day plan', desc: 'Your private FHC certification and your plan of action for the first month.', icon: Star },
      ],
    },
    inside: {
      label: 'Inside the club',
      title: 'What happens inside Freedom Homes Club',
      items: [
        { title: 'Off-market opportunities', desc: 'Property opportunities that never reach the portals, presented in the members’ area with their documentation and analysis. You choose what you take part in.' },
        { title: 'Know-how that counts', desc: 'FHC Academy takes you from a standing start to reading a property deal on your own judgement. Learn first; decide later.' },
        { title: 'Community and gatherings', desc: 'An active club of members and professionals, with live sessions, events and in-person bootcamps on the Costa Blanca.' },
      ],
    },
    philosophy: {
      label: 'Our philosophy',
      title: 'The principles behind how we work.',
      quote: 'Freedom Homes Club sits where traditional property expertise meets emerging digital infrastructure. We believe the future of the sector is being built now.',
      items: [
        { n: 'I', t: 'Clarity over complexity', d: 'We make sophisticated ideas simple without losing their depth.' },
        { n: 'II', t: 'Access on merit', d: 'Membership comes from professional fit, not from marketing.' },
        { n: 'III', t: 'A global outlook', d: 'Property is no longer a local business. Our community is open to professionals from many countries.' },
        { n: 'IV', t: 'Education first', d: 'Understanding always comes first.' },
        { n: 'V', t: 'Community as infrastructure', d: 'The network of members is worth as much as the knowledge we share.' },
        { n: 'VI', t: 'Transparency', d: 'What each membership includes, explained clearly.' },
        { n: 'VII', t: 'The long view', d: 'We build for decades, not for quarters.' },
      ],
    },
    assessment: {
      label: 'Suitability test',
      title: 'Do you have the profile to',
      titleAccent: 'join?',
      desc: 'Joining Freedom Homes Club starts with a suitability test that takes under two minutes. Then you choose a day and time for a call with the team, where we explain each membership, its fee and which tier suits you.',
      cta: 'Take the suitability test',
    },
    faq: {
      label: 'FAQ',
      title: 'Frequently asked questions',
      payment: 'Payment methods:',
      paymentDesc: 'Card or bank transfer.',
      items: [
        { q: 'What is the real difference between Access, PRO and Premium?', a: 'Access gives you membership of the club, the private community and introductory content. PRO adds advanced training, live specialist sessions and extended resources. Premium is the complete tier: everything in PRO, plus the in-person Digital Broker Bootcamp, the private FHC certification and weekly group mentoring.' },
        { q: 'What kind of opportunities are there inside the club?', a: 'Property opportunities that the team and its partners work on away from the portals. They are presented in the members’ area with their documentation and analysis, and each member decides freely, once trained, whether to take part and in which. Membership gives you access to the club; taking part is always a separate, voluntary decision.' },
        { q: 'How much does membership cost?', a: 'Each membership has its own annual fee, renewed from the date you join. We explain it in a short call with the team after you take the suitability test, so we can recommend the tier that genuinely suits you.' },
        { q: 'What exactly am I paying for?', a: 'The fee covers access to the club, the private community, the training content, and the sessions and events included in your tier — nothing else.' },
        { q: 'Is the certification official?', a: 'The FHC certification is a private, internal club credential with no official academic equivalence.' },
        { q: 'Do I need any previous experience?', a: 'No. The programme starts from scratch and guides you step by step through property tokenisation.' },
      ],
    },
    final: {
      title: 'The future of property',
      titleAccent: 'is already taking shape.',
      desc: 'The question is whether you watch it from the outside or learn it from within.',
      cta: 'Take the suitability test',
      note: 'After the test you can book your call straight away.',
    },
    team: {
      label: 'Who’s behind it',
      title: 'A founding team you can put a face to.',
      desc: 'Real people, with names and surnames, supporting every member from day one.',
      roles: ['CCO & Head of Sales', 'COO', 'Director of Training and Community'],
    },
    imgAlt: {
      who: 'Villa on the Costa Blanca that has hosted in-person club gatherings',
      philosophy: 'Interior of a villa on the Costa Blanca where the club has held gatherings',
    },
  },
};

/* ─── LANG CONTEXT ─── */
const LangCtx = createContext();

/* ─── FAQ ITEM ─── */
function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 hover:border-[#D4AF37]/40 transition-colors" data-testid={`faq-e-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-6 text-left group">
        <div className="flex items-start gap-4 pr-4">
          <HelpCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          <span className="font-heading text-base text-[#0F172A]">{q}</span>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-6 pl-14"><p className="font-body text-slate-600 leading-relaxed">{a}</p></div>}
    </div>
  );
}

/* ─── MAIN ─── */
export default function FreedomHomesE() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { lang } = useSiteLang();
  const t = texts[lang];




  const insideIcons = [KeyRound, BookOpen, Users];


  return (
    <LangCtx.Provider value={{ t, lang }}>
    <div className="min-h-screen bg-white pb-20 md:pb-0">

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center" data-testid="hero-e">
        <img src={IMG.hero} alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d1b]/85 via-[#0a1628]/75 to-[#060d1b]/95" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 backdrop-blur-sm px-5 py-2.5 mb-8">
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
            <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37]">{t.hero.badge}</span>
          </div>
          <h1 className="font-heading text-[2.6rem] sm:text-5xl lg:text-7xl font-medium text-white leading-tight mb-6" data-testid="hero-title-e">
            {t.hero.title} <span className="italic text-[#D4AF37]">{t.hero.titleAccent}</span>
          </h1>
          <p className="font-heading text-xl md:text-2xl text-white/85 italic mb-5 max-w-2xl mx-auto leading-snug">{t.hero.subtitle}</p>
          <p className="font-body text-base md:text-lg text-white/65 max-w-2xl mx-auto mb-10 leading-relaxed">{t.hero.desc}</p>
          <div className="flex justify-center mb-14">
            <Button onClick={() => setIsModalOpen(true)} data-testid="hero-cta-primary-e" className="h-14 px-6 sm:px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold tracking-wide rounded-none inline-flex items-center gap-2">
              {t.hero.cta1} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="inline-flex flex-wrap justify-center gap-x-8 gap-y-3">
            {[KeyRound, GraduationCap, Users].map((Icon, i) => (
              <div key={i} className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#D4AF37]/80" />
                <span className="font-body text-xs text-white/60 uppercase tracking-wider">{t.hero.badges[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block"><ChevronDown className="w-8 h-8 text-white/50" /></div>
      </section>

      {/* MARKET */}
      <section id="market" className="py-24 md:py-32 bg-white" data-testid="market-e">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.market.label}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">{t.market.title}</h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[Building2, Network, Sparkles].map((Icon, i) => (
              <div key={i} className="text-center p-8 border border-slate-100 hover:border-[#D4AF37]/30 transition-colors group">
                <span className="font-body text-xs text-[#D4AF37]/60 tracking-widest mb-4 block">{t.market.items[i].num}</span>
                <div className="w-14 h-14 mx-auto mb-6 bg-[#0F172A] rounded-full flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors"><Icon className="w-6 h-6 text-white" /></div>
                <h3 className="font-heading text-lg text-[#0F172A] mb-3">{t.market.items[i].title}</h3>
                <p className="font-body text-sm text-slate-500 leading-relaxed">{t.market.items[i].desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="who-e">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.who.label}</span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-6">{t.who.title} <span className="italic text-[#D4AF37]">{t.who.titleAccent}</span></h2>
              <div className="space-y-6">
                {[Target, Globe2, Eye].map((Icon, i) => (
                  <div key={i} className="flex gap-4 items-start" data-testid={`who-card-e-${i}`}>
                    <div className="w-10 h-10 bg-[#0F172A] rounded-full flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
                    <div>
                      <h3 className="font-heading text-lg text-[#0F172A] mb-1">{t.who.items[i].title}</h3>
                      <p className="font-body text-sm text-slate-500 leading-relaxed">{t.who.items[i].desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[320px] sm:h-[400px] lg:h-[500px] overflow-hidden">
              <img src={IMG.who} alt={t.imgAlt.who} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* INSIDE */}
      <section className="py-24 md:py-32 bg-[#0F172A]" data-testid="inside-e">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.inside.label}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">{t.inside.title}</h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {t.inside.items.map((c, i) => { const Icon = insideIcons[i]; return (
              <div key={i} className="bg-white/5 border border-white/10 p-8 text-center hover:border-[#D4AF37]/30 transition-colors">
                <div className="w-14 h-14 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center"><Icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="font-heading text-lg text-white mb-3">{c.title}</h3>
                <p className="font-body text-sm text-white/60 leading-relaxed">{c.desc}</p>
              </div>
            ); })}
          </div>
          {/* CTA intermedio después de Dentro del Club */}
          <div className="mt-12 text-center">
            <Button onClick={() => setIsModalOpen(true)} className="h-14 px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold tracking-wide rounded-none inline-flex items-center gap-2" data-testid="inside-cta">
              {t.assessment.cta}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="font-body text-xs text-white/30 mt-3">{lang === 'es' ? 'Menos de 2 minutos. Después, agendas tu llamada.' : 'Less than 2 minutes. Then you book your call.'}</p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32 bg-white" data-testid="philosophy-e">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.philosophy.label}</span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-6">{t.philosophy.title}</h2>
              <div className="border-l-2 border-[#D4AF37] pl-6 mb-8">
                <p className="font-heading text-lg text-slate-600 italic leading-relaxed">{t.philosophy.quote}</p>
              </div>
              <div className="relative h-[240px] sm:h-[280px] overflow-hidden"><img src={IMG.philosophy} alt={t.imgAlt.philosophy} loading="lazy" className="w-full h-full object-cover" /></div>
            </div>
            <div className="space-y-6">
              {t.philosophy.items.map((p, i) => (
                <div key={i} className="flex gap-4 items-start group">
                  <span className="font-heading text-sm text-[#D4AF37] min-w-[28px] pt-0.5">{p.n}</span>
                  <div>
                    <h4 className="font-heading text-base text-[#0F172A] mb-1 group-hover:text-[#D4AF37] transition-colors">{p.t}</h4>
                    <p className="font-body text-sm text-slate-500">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32 bg-[#0F172A]" data-testid="team-e">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.team.label}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">{t.team.title}</h2>
            <p className="font-body text-white/60 max-w-xl mx-auto leading-relaxed">{t.team.desc}</p>
          </div>
          <div className="mx-auto flex max-w-4xl flex-wrap justify-center gap-6">
            {TEAM_PHOTOS.map((p, i) => (
              <figure key={p.name} className="w-[calc(50%-12px)] text-center lg:w-[calc(33.333%-16px)]">
                <div className="aspect-square overflow-hidden border border-[#D4AF37]/25 bg-[#0a1628] max-w-[220px] mx-auto">
                  <img src={p.img} alt={`${p.name}, ${t.team.roles[i]}`} loading="lazy" width="420" height="420" className="w-full h-full object-cover" />
                </div>
                <figcaption className="mt-4">
                  <p className="font-heading text-base sm:text-lg text-white leading-tight">{p.name}</p>
                  <p className="font-body text-[11px] sm:text-xs uppercase tracking-wider text-[#D4AF37] mt-1 leading-snug">{t.team.roles[i]}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PROFILE ASSESSMENT */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="suitability-e">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.assessment.label}</span>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-6">{t.assessment.title} <span className="italic text-[#D4AF37]">{t.assessment.titleAccent}</span></h2>
          <p className="font-body text-slate-500 max-w-lg mx-auto mb-10 leading-relaxed">{t.assessment.desc}</p>
          <Button onClick={() => setIsModalOpen(true)} data-testid="suitability-cta-e" className="h-16 px-6 sm:px-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold tracking-wide rounded-none text-base inline-flex items-center gap-2">
            {t.assessment.cta} <ArrowUpRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="faq-e">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">{t.faq.label}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">{t.faq.title}</h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>
          <div className="space-y-4">
            {t.faq.items.map((faq, i) => <FAQItem key={`${lang}-${i}`} q={faq.q} a={faq.a} idx={i} />)}
          </div>
          <div className="mt-8 p-6 bg-[#0F172A] flex items-center gap-4">
            <CreditCard className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
            <p className="font-body text-sm text-white/70"><span className="text-[#D4AF37] font-semibold">{t.faq.payment}</span> {t.faq.paymentDesc}</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 bg-gradient-to-b from-[#0a1628] to-[#060d1b]" data-testid="final-cta-e">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 leading-tight">{t.final.title}<br /><span className="text-[#D4AF37] italic">{t.final.titleAccent}</span></h2>
          <p className="font-body text-white/60 mb-10 max-w-lg mx-auto">{t.final.desc}</p>
          <Button onClick={() => setIsModalOpen(true)} data-testid="final-cta-e" className="h-16 px-6 sm:px-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold tracking-wide rounded-none text-base inline-flex items-center gap-2">
            {t.final.cta} <ArrowUpRight className="w-5 h-5" />
          </Button>
          <p className="font-body text-sm text-white/30 mt-6">{t.final.note}</p>
        </div>
      </section>


      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg md:hidden">
        <div className="flex gap-2">
          <Button onClick={() => setIsModalOpen(true)} className="w-full h-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold rounded-none text-sm" data-testid="mobile-cta-e">{t.nav.cta}</Button>
        </div>
      </div>

      <QualificationFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} source="freedom-homes-e" lang={lang} />
    </div>
    </LangCtx.Provider>
  );
}
